import { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { PageContainer } from '@/components/page-container';
import { ChannelDetailView } from '@/components/channel-detail';
import { DailyUsageChart } from '@/components/dashboard/daily-usage-chart';
import { unitLabel, unitTitle } from '@/lib/usage-unit';
import { useWorkspace } from '@/contexts/workspace-context';
import { workspaceUrl } from '@/lib/workspace-url';
import { useDashboardMetrics, type RangePreset, type CustomDates } from './dashboard/use-dashboard-metrics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/date-range-picker';
import { AgentSetupCard } from '@/components/agent-setup-card';
import { ResetsOn } from '@/components/dashboard/resets-on';
import { useQuotaWindowRollover } from '@/components/billing/queries';

export function DashboardPage() {
  const { ws, channelId } = useParams();
  const navigate = useNavigate();
  const { channels, activeWorkspace, refreshChannels, channelsLoading, orgUsage } =
    useWorkspace();
  const { onConnectClick } = useOutletContext<{ onConnectClick: () => void }>();

  const [preset, setPreset] = useState<RangePreset>('period');
  const [custom, setCustom] = useState<CustomDates>({ start: '', end: '' });
  const [channelTab, setChannelTab] = useState<'all' | 'active' | 'disconnected'>('all');
  const { data, loading } = useDashboardMetrics(preset, custom, channelTab, orgUsage?.periodStartsAt);
  useQuotaWindowRollover(orgUsage?.resetsAt);

  if (channelsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Channel detail view
  if (channelId) {
    const channel = channels.find((a) => a.id === channelId);
    if (!channel) {
      if (ws) navigate(workspaceUrl(ws), { replace: true });
      return null;
    }

    return (
      <ChannelDetailView
        key={channel.id}
        channel={channel}
        userRole={activeWorkspace?.role ?? 'member'}
        onChannelUpdated={refreshChannels}
        onDisconnected={() => {
          refreshChannels();
          if (ws) navigate(workspaceUrl(ws));
        }}
      />
    );
  }

  // Metrics dashboard view (no channelId). Channel counts come from the live
  // workspace list (keyed off the durable channelStatus lifecycle field), so the
  // tabs stay correct even when a Meta-health probe flips metaConnected.
  const channelCounts = {
    all: channels.length,
    active: channels.filter((c) => c.channelStatus === 'connected').length,
    disconnected: channels.filter((c) => c.channelStatus === 'disconnected').length,
  };
  // AIT-436: one number in the org's own meter. The stat card and the chart
  // used to carry an all/inbound/outbound filter each; actions have no
  // inbound, so both filters are gone and the unit names itself.
  const unit = data?.usageUnit;
  const usedValue = data?.used ?? 0;
  return (
    <PageContainer variant="scroll" className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
      </header>
      <AgentSetupCard />
      <div className="flex flex-wrap items-center gap-4">
        <Tabs value={preset} onValueChange={(v) => setPreset(v as RangePreset)}>
          <TabsList>
            <TabsTrigger value="period">Billing period</TabsTrigger>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>

        {preset === 'custom' && <DateRangePicker value={custom} onChange={setCustom} />}

        {/* Channel-status filter scopes the whole page (Channels count, usage,
            daily chart) to active vs disconnected channels. Pushed right. */}
        <Tabs className="md:ml-auto" value={channelTab} onValueChange={(v) => setChannelTab(v as typeof channelTab)}>
          <TabsList aria-label="Channels filter">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="disconnected">Disconnected</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>Channels</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="text-3xl font-bold"
              role="status"
              aria-label={`Channels count ${channelCounts[channelTab]}`}
            >
              <NumberFlow value={channelCounts[channelTab]} className="tabular-nums" />
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={onConnectClick}>
              Connect Channel
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>{unitTitle(unit)}</CardTitle>
            {preset === 'period' && <ResetsOn resetsAt={orgUsage?.resetsAt} />}
          </CardHeader>
          <CardContent>
            <div
              className="text-3xl font-bold"
              role="status"
              aria-label={`${unitTitle(unit)} count ${usedValue}`}
            >
              <NumberFlow value={usedValue} className="tabular-nums" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle>Daily {unitTitle(unit)}</CardTitle>
        </CardHeader>
        <CardContent>
          <DailyUsageChart data={data?.daily ?? []} unitLabel={unitLabel(unit)} loading={loading} />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
